import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';

type UseDebouncedMutationProps<TData, TVariables> = {
  mutationFn: (data: TVariables) => Promise<TData>;
  onSuccess?: (data: TData) => void;
  debounceTime?: number;
};

export function useDebouncedMutation<TData, TVariables>({
  mutationFn,
  onSuccess,
  debounceTime = 300, // Default debounce time
}: UseDebouncedMutationProps<TData, TVariables>) {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const mutation = useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
  });

  const handleDebouncedMutation = (data: TVariables) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      mutation.mutate(data);
    }, debounceTime);
  };

  return { handleDebouncedMutation, mutation };
}
